<template>
  <iColumn
    v-if="visible"
    gap="standard"
    width="fill"
    :class="{'questionnaire-field-followup': isFollowUp}"
  >
    <iColumn gap="extraSmall" width="fill">
      <iText :variant="labelVariant" font-weight="dark">
        {{ labelWithHint }}
      </iText>
      <iText v-if="field.note" variant="subtle" font-size="small">
        {{ field.note }}
      </iText>
    </iColumn>

    <iTextInput
      v-if="field.type === FIELD_TYPE_TEXT || field.type === FIELD_TYPE_EMAIL"
      :model-value="value"
      :type="field.type === FIELD_TYPE_EMAIL ? 'email' : 'text'"
      :placeholder="field.placeholder || ''"
      width="fill"
      @update:model-value="updateScalarValue"
    />

    <iTextArea
      v-else-if="field.type === FIELD_TYPE_TEXTAREA"
      :model-value="value"
      :placeholder="field.placeholder || ''"
      :rows="field.textarea_rows || 6"
      width="fill"
      @update:model-value="updateScalarValue"
    />

    <iSelect
      v-else-if="field.type === FIELD_TYPE_DROPDOWN"
      :model-value="value"
      :items="field.options"
      value-field="value"
      text-field="label"
      :empty-string="field.placeholder || 'Select...'"
      return-value
      width="fill"
      @update:model-value="updateScalarValue"
    />

    <iRow v-else-if="field.type === FIELD_TYPE_SINGLE_SELECT" gap="extraSmall" wrap="wrap">
      <button
        v-for="option in field.options"
        :key="option.value"
        type="button"
        class="option-button"
        :class="{'option-button-selected': value === option.value}"
        @click="updateScalarValue(option.value)"
      >
        <span class="option-button-label">{{ option.label }}</span>
      </button>
    </iRow>

    <iRow v-else-if="field.type === FIELD_TYPE_MULTI_SELECT" gap="extraSmall" wrap="wrap">
      <button
        v-for="option in field.options"
        :key="option.value"
        type="button"
        class="option-button"
        :class="{'option-button-selected': isOptionSelected(option.value)}"
        :style="optionButtonStyle(option)"
        @click="toggleOptionValue(option.value)"
      >
        <span v-if="hasSwatches(option)" class="option-button-swatches">
          <span
            v-for="(swatch, swatchIndex) in option.swatches"
            :key="swatchIndex"
            class="option-button-swatch"
            :style="{backgroundColor: swatch}"
          />
        </span>
        <span class="option-button-label">{{ option.label }}</span>
      </button>
    </iRow>

    <QuestionnaireSubscriptionTiers
      v-else-if="field.type === FIELD_TYPE_SUBSCRIPTION_TIERS"
      :section-key="sectionKey"
      :field="field"
    />

    <QuestionnaireSocialLinks
      v-else-if="field.type === FIELD_TYPE_SOCIAL_LINKS"
      :section-key="sectionKey"
      :field="field"
    />

    <QuestionnaireRepeaterText
      v-else-if="field.type === FIELD_TYPE_REPEATER_TEXT"
      :section-key="sectionKey"
      :field="field"
    />

    <QuestionnaireColorPickerList
      v-else-if="field.type === FIELD_TYPE_COLOR_PICKER_LIST"
      :section-key="sectionKey"
      :field="field"
    />

    <QuestionnaireFileUpload
      v-else-if="field.type === FIELD_TYPE_FILE_UPLOAD"
      :section-key="sectionKey"
      :field="field"
    />

    <QuestionnaireVibePicker
      v-else-if="field.type === FIELD_TYPE_VIBE_PICKER"
      :section-key="sectionKey"
      :field="field"
    />
  </iColumn>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useQuestionnaireStore } from "@/stores/questionnaire-store";
import QuestionnaireSubscriptionTiers from "@/components/questionnaire/QuestionnaireSubscriptionTiers.vue";
import QuestionnaireSocialLinks from "@/components/questionnaire/QuestionnaireSocialLinks.vue";
import QuestionnaireRepeaterText from "@/components/questionnaire/QuestionnaireRepeaterText.vue";
import QuestionnaireColorPickerList from "@/components/questionnaire/QuestionnaireColorPickerList.vue";
import QuestionnaireFileUpload from "@/components/questionnaire/QuestionnaireFileUpload.vue";
import QuestionnaireVibePicker from "@/components/questionnaire/QuestionnaireVibePicker.vue";
import {
  FIELD_TYPE_COLOR_PICKER_LIST,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_EMAIL,
  FIELD_TYPE_FILE_UPLOAD,
  FIELD_TYPE_MULTI_SELECT,
  FIELD_TYPE_REPEATER_TEXT,
  FIELD_TYPE_SINGLE_SELECT,
  FIELD_TYPE_SOCIAL_LINKS,
  FIELD_TYPE_SUBSCRIPTION_TIERS,
  FIELD_TYPE_TEXT,
  FIELD_TYPE_TEXTAREA,
  FIELD_TYPE_VIBE_PICKER,
  isFieldVisible,
} from "%/questionnaire/schema-helpers";

export default {
  name: "QuestionnaireField",
  components: {
    QuestionnaireSubscriptionTiers,
    QuestionnaireSocialLinks,
    QuestionnaireRepeaterText,
    QuestionnaireColorPickerList,
    QuestionnaireFileUpload,
    QuestionnaireVibePicker,
  },
  props: {
    sectionKey: {
      type: String,
      required: true,
    },
    field: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      FIELD_TYPE_TEXT,
      FIELD_TYPE_EMAIL,
      FIELD_TYPE_TEXTAREA,
      FIELD_TYPE_DROPDOWN,
      FIELD_TYPE_SINGLE_SELECT,
      FIELD_TYPE_MULTI_SELECT,
      FIELD_TYPE_SUBSCRIPTION_TIERS,
      FIELD_TYPE_SOCIAL_LINKS,
      FIELD_TYPE_REPEATER_TEXT,
      FIELD_TYPE_COLOR_PICKER_LIST,
      FIELD_TYPE_FILE_UPLOAD,
      FIELD_TYPE_VIBE_PICKER,
    };
  },
  computed: {
    ...mapState(useQuestionnaireStore, [
      "responseData",
      "hasAttemptedToAdvance",
      "missingFieldKeysForCurrentStep",
    ]),
    sectionData() {
      return this.responseData[this.sectionKey] || {};
    },
    value() {
      return this.sectionData[this.field.key];
    },
    visible() {
      return isFieldVisible(this.field, this.responseData);
    },
    // A conditional field (e.g. the "Other" text box) reads as part of the
    // question that revealed it, so it sits closer to that question than the
    // standard gap between separate questions.
    isFollowUp() {
      return Boolean(this.field.visible_when);
    },
    labelWithHint() {
      if (this.field.hint) {
        return `${this.field.label} (${this.field.hint})`;
      }
      return this.field.label;
    },
    labelVariant() {
      if (!this.hasAttemptedToAdvance) {
        return "standard";
      }
      const key = `${this.sectionKey}.${this.field.key}`;
      return this.missingFieldKeysForCurrentStep.includes(key) ? "error" : "standard";
    },
  },
  methods: {
    ...mapActions(useQuestionnaireStore, [
      "toggleMultiSelectValue",
      "setSingleSelectValue",
    ]),
    updateScalarValue(newValue) {
      this.setSingleSelectValue(this.sectionKey, this.field.key, newValue);
    },
    toggleOptionValue(optionValue) {
      this.toggleMultiSelectValue(this.sectionKey, this.field.key, optionValue);
    },
    isOptionSelected(optionValue) {
      const list = this.value;
      return Array.isArray(list) && list.includes(optionValue);
    },
    hasSwatches(option) {
      return Array.isArray(option.swatches) && option.swatches.length > 0;
    },
    optionButtonStyle(option) {
      if (option.font_family) {
        return { fontFamily: option.font_family };
      }
      return {};
    },
  },
  styleGuide: () => ({
    brandedColor: { "color.font": "branded" },
    surfaceColor: { "color.background": "subtle" },
    surfaceTextColor: { "color.font": "standard" },
  }),
};
</script>

<style scoped lang="scss">
.questionnaire-field-followup {
    // Pull the revealed field up toward the question it belongs to, tightening
    // the section's standard between-question gap for this reveal only.
    margin-top: -28px;
}

.option-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: 1px solid v-bind('$getStyles.surfaceColor');
    border-radius: 8px;
    background-color: v-bind('$getStyles.surfaceColor');
    color: v-bind('$getStyles.surfaceTextColor');
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
        border-color: v-bind('$getStyles.brandedColor');
    }
}

.option-button-selected {
    background-color: v-bind('$getStyles.brandedColor');
    border-color: v-bind('$getStyles.brandedColor');
    color: #fff;
}

.option-button-swatches {
    display: inline-flex;
    gap: 2px;
}

.option-button-swatch {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 1px solid rgba(0, 0, 0, 0.15);
}

.option-button-label {
    white-space: nowrap;
}
</style>
